import { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { RefreshCw, BarChart3 } from "lucide-react";
import { Button } from "components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { useStatisticsData } from "./hooks/useStatisticsData";
import { StatisticsFilters } from "./types/statistics.types";
import {
  StatisticsKpiCards,
  VolumeTimeSeriesChart,
  PhaseDurationChart,
  BuildingDistributionChart,
  CategoryDistributionChart,
  SourceDistributionChart,
  ClusterBuildingAnalysis,
  ActionItemsTable,
  BuildingVolumeTrendsCharts,
  ExpenseDistributionChart,
  TopTenantsTable,
} from "./components";

export const HelpdeskStatisticsPage = () => {
  const [filters, setFilters] = useState<StatisticsFilters>({
    granularity: "week",
  });

  const { data, loading, isLoading, refresh } = useStatisticsData(filters);

  // Initialize sticky top as 0 for this page (no global SearchBar)
  useEffect(() => {
    document.documentElement.style.setProperty("--sticky-top", "0px");
  }, []);

  const handleGranularityChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      granularity: value as "day" | "week" | "month",
    }));
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Helpdesk Statistieken &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-6 px-4 lg:px-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Helpdesk Statistieken
                </h1>
                <p className="text-sm text-muted-foreground">
                  Inzicht in prestaties en doorlooptijden (vanaf 01/11/2025)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Granularity selector */}
              <Select
                value={filters.granularity}
                onValueChange={handleGranularityChange}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Periode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Per dag</SelectItem>
                  <SelectItem value="week">Per week</SelectItem>
                  <SelectItem value="month">Per maand</SelectItem>
                </SelectContent>
              </Select>

              {/* Refresh button */}
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={isLoading}
                className="gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
                Vernieuwen
              </Button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="mb-6">
            <StatisticsKpiCards
              dashboard={data.dashboard}
              throughput={data.throughput}
              loading={loading.dashboard || loading.throughput}
            />
          </div>

          {/* Charts Row 1: Volume & Uitgaven.
              "Tijd per fase" stond hier; die is samen met alles eronder naar
              beneden geschoven om bovenaan plaats te maken voor de uitgaven en
              de top-huurders. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <VolumeTimeSeriesChart
              data={data.timeSeries}
              loading={loading.timeSeries}
            />
            <ExpenseDistributionChart />
          </div>

          {/* Top huurders (melders met de meeste tickets) */}
          <div className="mb-6">
            <TopTenantsTable />
          </div>

          {/* Tijd per fase */}
          <div className="mb-6">
            <PhaseDurationChart
              data={data.phaseDurations}
              loading={loading.phaseDurations}
            />
          </div>

          {/* Building Volume Trends - Rising/Declining */}
          <div className="mb-6">
            <BuildingVolumeTrendsCharts />
          </div>

          {/* Charts Row 2: Building & Category Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <BuildingDistributionChart />
            <CategoryDistributionChart />
          </div>

          {/* Charts Row 3: Source Distribution */}
          <div className="mb-6">
            <SourceDistributionChart />
          </div>

          {/* Cluster & Building Analysis */}
          <div className="mb-6">
            <ClusterBuildingAnalysis />
          </div>

          {/* Action Items Table */}
          <div className="mb-6">
            <ActionItemsTable
              data={data.actionItems}
              loading={loading.actionItems}
            />
          </div>
        </div>
      </div>
    </>
  );
};
